/**
 * Escalation Queue Page
 * AIDLC Administrative Backend
 *
 * Dan's decision-making interface for escalations with price-tagged
 * options. View open escalations, compare token-cost options side by
 * side, and resolve with notes. Consumes /api/escalations endpoints.
 *
 * Personas: Executive, Ops, Orchestrator, Gatekeeper
 */

import { useState, useEffect, useCallback } from 'react';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';
import { Button } from '../shared/Button';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import { fmtTokens, fmtDateTime } from '../../utils/formatters';
import type {
  Escalation,
  EscalationSeverity,
  EscalationType,
  EscalationStatus,
  EscalationOption,
} from '../../services/orchestrationService';
import {
  getEscalations,
  resolveEscalation,
} from '../../services/orchestrationService';

/** Severity badge config */
const severityConfig: Record<EscalationSeverity, { label: string; variant: 'info' | 'warning' | 'danger' | 'neutral' }> = {
  low: { label: 'Low', variant: 'info' },
  medium: { label: 'Medium', variant: 'warning' },
  high: { label: 'High', variant: 'danger' },
  critical: { label: 'Critical', variant: 'danger' },
};

/** Type display labels (matching backend valid types) */
const typeLabels: Record<EscalationType, string> = {
  dev_qa_disagreement: 'Dev/QA Disagreement',
  scope_creep: 'Scope Creep',
  velocity_drop: 'Velocity Drop',
  budget_insufficient: 'Budget Insufficient',
  priority_conflict: 'Priority Conflict',
  quality_gate_failure: 'Quality Gate Failure',
  agent_blocked: 'Agent Blocked',
};

/** Status badge config */
const statusConfig: Record<EscalationStatus, { label: string; variant: 'danger' | 'warning' | 'success' | 'neutral' }> = {
  open: { label: 'Open', variant: 'danger' },
  in_review: { label: 'In Review', variant: 'warning' },
  resolved: { label: 'Resolved', variant: 'success' },
  dismissed: { label: 'Dismissed', variant: 'neutral' },
};

type FilterOption = 'all' | EscalationStatus;

// ── Main Component ───────────────────────────────────────────────────

export function EscalationQueuePage() {
  const [escalations, setEscalations] = useState<Escalation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterOption>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const loadEscalations = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getEscalations();
      setEscalations(data);
    } catch (err) {
      console.error('[EscalationQueue] Failed to load:', err);
      setError('Failed to load escalations. Please check the backend connection.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEscalations();
  }, [loadEscalations]);

  const filteredEscalations =
    filter === 'all' ? escalations : escalations.filter((e) => e.status === filter);

  const counts = {
    all: escalations.length,
    open: escalations.filter((e) => e.status === 'open').length,
    in_review: escalations.filter((e) => e.status === 'in_review').length,
    resolved: escalations.filter((e) => e.status === 'resolved').length,
    dismissed: escalations.filter((e) => e.status === 'dismissed').length,
  };

  const selectedEscalation = selectedId
    ? escalations.find((e) => e.id === selectedId) || null
    : null;

  const handleResolved = async () => {
    await loadEscalations();
    setSelectedId(null);
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading escalation queue..." />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="text-red-500 mb-4">
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-gray-700 font-medium mb-2">Unable to Load Escalations</p>
        <p className="text-sm text-gray-500 mb-4 max-w-md text-center">{error}</p>
        <button
          onClick={loadEscalations}
          className="px-4 py-2 bg-ldaja-blue text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Detail View
  if (selectedEscalation) {
    return (
      <EscalationDetailView
        escalation={selectedEscalation}
        onBack={() => setSelectedId(null)}
        onResolved={handleResolved}
      />
    );
  }

  // List View
  return (
    <div>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-xl font-bold text-ldaja-black">Escalation Queue</h1>
            <p className="text-sm text-gray-500 mt-1">
              Review and resolve escalated items requiring human decisions.
            </p>
          </div>
          {counts.open > 0 && (
            <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-bold bg-red-500 text-white">
              {counts.open} open
            </span>
          )}
        </div>
        <button
          onClick={loadEscalations}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-1 mb-6">
        {([
          { key: 'all' as FilterOption, label: 'All' },
          { key: 'open' as FilterOption, label: 'Open' },
          { key: 'in_review' as FilterOption, label: 'In Review' },
          { key: 'resolved' as FilterOption, label: 'Resolved' },
          { key: 'dismissed' as FilterOption, label: 'Dismissed' },
        ]).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              filter === key
                ? 'bg-ldaja-blue text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {label} ({counts[key]})
          </button>
        ))}
      </div>

      {/* Escalation Cards */}
      {filteredEscalations.length === 0 ? (
        <EmptyState
          title="No escalations found"
          description={
            filter === 'all'
              ? 'No escalations have been raised. The team is running smoothly.'
              : `No escalations with status "${statusConfig[filter as EscalationStatus]?.label || filter}".`
          }
          action={
            filter !== 'all' ? (
              <button
                onClick={() => setFilter('all')}
                className="text-sm text-ldaja-blue hover:underline"
              >
                Clear filter
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-3">
          {filteredEscalations.map((escalation) => (
            <EscalationCard
              key={escalation.id}
              escalation={escalation}
              onClick={() => setSelectedId(escalation.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Escalation Card ──────────────────────────────────────────────────

function EscalationCard({
  escalation,
  onClick,
}: {
  escalation: Escalation;
  onClick: () => void;
}) {
  const severity = severityConfig[escalation.severity] || severityConfig.low;
  const status = statusConfig[escalation.status] || statusConfig.open;
  const typeLabel = typeLabels[escalation.type] || escalation.type;
  const situationPreview =
    escalation.situation.length > 100
      ? escalation.situation.slice(0, 100) + '...'
      : escalation.situation;

  return (
    <div
      onClick={onClick}
      className="bg-white border border-gray-200 rounded-lg p-4 hover:border-ldaja-blue/40 hover:shadow-md transition-all cursor-pointer group"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={severity.variant} dot>
            {severity.label}
          </Badge>
          <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-medium">
            {typeLabel}
          </span>
          <Badge variant={status.variant}>
            {status.label}
          </Badge>
        </div>
        <span className="text-xs text-gray-400 shrink-0 ml-2">
          {escalation.options.length} option{escalation.options.length !== 1 ? 's' : ''}
        </span>
      </div>

      <p className="text-sm text-gray-700 mb-2 group-hover:text-ldaja-blue transition-colors">
        {situationPreview}
      </p>

      <div className="flex items-center justify-between text-xs text-gray-400">
        <span>
          Raised by <span className="font-medium text-gray-500">{escalation.raisedBy}</span>
        </span>
        <span>{fmtDateTime(escalation.createdAt)}</span>
      </div>
    </div>
  );
}

// ── Escalation Detail View ───────────────────────────────────────────

function EscalationDetailView({
  escalation,
  onBack,
  onResolved,
}: {
  escalation: Escalation;
  onBack: () => void;
  onResolved: () => void;
}) {
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [isResolving, setIsResolving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const severity = severityConfig[escalation.severity] || severityConfig.low;
  const status = statusConfig[escalation.status] || statusConfig.open;
  const typeLabel = typeLabels[escalation.type] || escalation.type;
  const isResolved = escalation.status === 'resolved' || escalation.status === 'dismissed';

  const handleChooseOption = (idx: number) => {
    setSelectedOption(idx);
    setShowConfirm(true);
  };

  const handleConfirmResolve = async () => {
    if (selectedOption === null) return;
    setIsResolving(true);
    setActionError(null);
    try {
      await resolveEscalation(escalation.id, {
        chosenOptionIndex: selectedOption,
        notes: notes.trim(),
        resolvedBy: 'CEO',
      });
      onResolved();
    } catch (err) {
      console.error('[EscalationQueue] Failed to resolve:', err);
      setActionError('Failed to resolve escalation. Please try again.');
    } finally {
      setIsResolving(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
    setSelectedOption(null);
    setNotes('');
  };

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-ldaja-blue transition-colors mb-4"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M15 19l-7-7 7-7" />
        </svg>
        Back to Queue
      </button>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Badge variant={severity.variant} dot>
              {severity.label}
            </Badge>
            <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-medium">
              {typeLabel}
            </span>
            <Badge variant={status.variant}>
              {status.label}
            </Badge>
          </div>
          <p className="text-sm text-gray-500">
            Raised by <span className="font-medium text-gray-700">{escalation.raisedBy}</span>
            {' '}&middot; {fmtDateTime(escalation.createdAt)}
          </p>
        </div>
      </div>

      {/* Action Error */}
      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-700 text-xs">{actionError}</p>
          <button onClick={() => setActionError(null)} className="text-red-600 text-xs underline mt-1">
            Dismiss
          </button>
        </div>
      )}

      {/* Situation */}
      <Card title="Situation" className="mb-6">
        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{escalation.situation}</p>
      </Card>

      {/* Resolution Info (if resolved) */}
      {isResolved && escalation.resolution && (
        <Card title="Resolution" className="mb-6">
          <div className="space-y-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-xs text-green-600 font-medium uppercase tracking-wide mb-1">Chosen Option</p>
              <p className="text-sm font-semibold text-green-800">
                {escalation.options[escalation.resolution.chosenOptionIndex]?.label || `Option ${escalation.resolution.chosenOptionIndex + 1}`}
              </p>
              <p className="text-sm text-green-700 mt-1">
                {escalation.options[escalation.resolution.chosenOptionIndex]?.description}
              </p>
              <p className="text-xs text-green-600 mt-2">
                Token cost: {fmtTokens(escalation.options[escalation.resolution.chosenOptionIndex]?.tokenCost || 0)}
              </p>
            </div>
            {escalation.resolution.notes && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Resolution Notes</p>
                <p className="text-sm text-gray-700">{escalation.resolution.notes}</p>
              </div>
            )}
            <div className="flex items-center gap-4 text-xs text-gray-400">
              {escalation.resolution.resolvedBy && (
                <span>Resolved by: <span className="font-medium text-gray-500">{escalation.resolution.resolvedBy}</span></span>
              )}
              {escalation.resolution.resolvedAt && (
                <span>{fmtDateTime(escalation.resolution.resolvedAt)}</span>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Options Comparison */}
      <Card
        title="Options"
        subtitle={isResolved ? 'Options that were available' : 'Compare options and choose one to resolve'}
        className="mb-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {escalation.options.map((option, idx) => (
            <OptionCard
              key={idx}
              option={option}
              index={idx}
              isChosen={isResolved && escalation.resolution?.chosenOptionIndex === idx}
              disabled={isResolved}
              onChoose={() => handleChooseOption(idx)}
            />
          ))}
        </div>
      </Card>

      {/* Confirmation Modal (inline) */}
      {showConfirm && selectedOption !== null && !isResolved && (
        <Card title="Confirm Resolution" className="mb-6">
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-600 font-medium mb-1">Selected Option</p>
              <p className="text-sm font-semibold text-blue-800">
                {escalation.options[selectedOption]?.label}
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Token cost: <span className="font-bold">{fmtTokens(escalation.options[selectedOption]?.tokenCost || 0)}</span>
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Resolution Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30 focus:border-ldaja-blue"
                placeholder="Add context about your decision..."
              />
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="primary"
                size="md"
                isLoading={isResolving}
                onClick={handleConfirmResolve}
                className="bg-green-600 hover:bg-green-700 focus:ring-green-500/50"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M5 13l4 4L19 7" />
                </svg>
                Confirm Resolution
              </Button>
              <Button variant="secondary" size="md" onClick={handleCancelConfirm}>
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Option Card ──────────────────────────────────────────────────────

function OptionCard({
  option,
  isChosen,
  disabled,
  onChoose,
}: {
  option: EscalationOption;
  index: number;
  isChosen: boolean;
  disabled: boolean;
  onChoose: () => void;
}) {
  return (
    <div
      className={`rounded-lg border p-4 transition-all ${
        isChosen
          ? 'border-green-300 bg-green-50 ring-2 ring-green-200'
          : 'border-gray-200 bg-white hover:border-ldaja-blue/30 hover:shadow-sm'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <h4 className="text-sm font-semibold text-ldaja-black">{option.label}</h4>
        {option.recommended && (
          <Badge variant="success">Recommended</Badge>
        )}
        {isChosen && (
          <Badge variant="success" dot>Chosen</Badge>
        )}
      </div>

      {/* Description */}
      <p className="text-xs text-gray-600 mb-3 leading-relaxed">{option.description}</p>

      {/* Token Cost - prominent */}
      <div className="bg-gray-50 rounded-md p-3 mb-3 text-center">
        <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">Token Cost</p>
        <p className="text-2xl font-bold text-ldaja-blue mt-0.5">{fmtTokens(option.tokenCost)}</p>
      </div>

      {/* Impact */}
      <div className="mb-3">
        <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium mb-1">Impact</p>
        <p className="text-xs text-gray-600">{option.impact}</p>
      </div>

      {/* Action */}
      {!disabled && (
        <Button
          variant="primary"
          size="sm"
          onClick={onChoose}
          className="w-full"
        >
          Choose This Option
        </Button>
      )}
    </div>
  );
}
